/**
 * icons.tsx — Rivmoon Console 自绘 SVG 图标库
 *
 * 移植自原型 design/Novel-admin/src/icons.jsx：Lucide 风格、stroke-based、currentColor、24×24 viewBox。
 * 用法：`import { Icons } from '~/lib/icons'` 后 `<Icons.IconHome size={16}/>`；或具名 `import { IconHome }`。
 */

import type { SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'fill'> {
  /** 像素尺寸，默认 16 */
  size?: number;
  /** stroke 宽度，默认 1.5 */
  sw?: number;
  fill?: string;
}

/** 基础包装：统一 viewBox / stroke / 圆角线帽 */
function I({ size = 16, sw = 1.5, fill = 'none', children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {children}
    </svg>
  );
}

// Navigation
const IconHome = (p: IconProps) => (
  <I {...p}>
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5" />
  </I>
);
const IconApps = (p: IconProps) => (
  <I {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </I>
);
const IconBook = (p: IconProps) => (
  <I {...p}>
    <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H19a1 1 0 0 1 1 1v15.5a.5.5 0 0 1-.5.5H5.5A1.5 1.5 0 0 1 4 18.5z" />
    <path d="M4 18.5A1.5 1.5 0 0 1 5.5 17H20" />
  </I>
);
const IconWallet = (p: IconProps) => (
  <I {...p}>
    <rect x="3" y="6" width="18" height="14" rx="2" />
    <path d="M3 10h18" />
    <path d="M16 15h2" />
  </I>
);
const IconUsers = (p: IconProps) => (
  <I {...p}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    <circle cx="17" cy="9" r="2.5" />
    <path d="M16 14c2.8 0 5 2 5 5" />
  </I>
);
const IconMsg = (p: IconProps) => (
  <I {...p}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />
  </I>
);
const IconBell = (p: IconProps) => (
  <I {...p}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </I>
);
const IconLayers = (p: IconProps) => (
  <I {...p}>
    <path d="m12 3 9 5-9 5-9-5z" />
    <path d="m3 13 9 5 9-5" />
    <path d="m3 18 9 5 9-5" />
  </I>
);
const IconChart = (p: IconProps) => (
  <I {...p}>
    <path d="M3 3v18h18" />
    <path d="m7 14 3-3 4 4 5-6" />
  </I>
);
const IconCog = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </I>
);

// Actions
const IconSearch = (p: IconProps) => (
  <I {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </I>
);
const IconPlus = (p: IconProps) => (
  <I {...p}>
    <path d="M12 5v14M5 12h14" />
  </I>
);
const IconCheck = (p: IconProps) => (
  <I {...p}>
    <path d="M20 6 9 17l-5-5" />
  </I>
);
const IconX = (p: IconProps) => (
  <I {...p}>
    <path d="M18 6 6 18M6 6l12 12" />
  </I>
);
const IconFilter = (p: IconProps) => (
  <I {...p}>
    <path d="M3 4h18l-7 9v6l-4 2v-8z" />
  </I>
);
const IconDownload = (p: IconProps) => (
  <I {...p}>
    <path d="M12 3v12m0 0 4-4m-4 4-4-4" />
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
  </I>
);
const IconUpload = (p: IconProps) => (
  <I {...p}>
    <path d="M12 21V9m0 0-4 4m4-4 4 4" />
    <path d="M4 7V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2" />
  </I>
);
const IconMore = (p: IconProps) => (
  <I {...p}>
    <circle cx="5" cy="12" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
  </I>
);
const IconMoreV = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="19" r="1" />
  </I>
);
const IconEdit = (p: IconProps) => (
  <I {...p}>
    <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" />
  </I>
);
const IconTrash = (p: IconProps) => (
  <I {...p}>
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </I>
);
const IconEye = (p: IconProps) => (
  <I {...p}>
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </I>
);
// 眼睛-划线（隐藏密码态）：移植自 design/Novel-admin/Login.html 密码可见性 toggle 的 hide 图标
const IconEyeOff = (p: IconProps) => (
  <I {...p}>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M6.61 6.61A18.5 18.5 0 0 0 2 12s3.5 7 10 7a9.12 9.12 0 0 0 3.5-.68" />
    <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </I>
);
const IconCopy = (p: IconProps) => (
  <I {...p}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </I>
);
const IconRefresh = (p: IconProps) => (
  <I {...p}>
    <path d="M21 12a9 9 0 1 1-9-9c2.5 0 4.7 1 6.4 2.6L21 8" />
    <path d="M21 3v5h-5" />
  </I>
);

// Arrows
const IconChevDown = (p: IconProps) => (
  <I {...p}>
    <path d="m6 9 6 6 6-6" />
  </I>
);
const IconChevUp = (p: IconProps) => (
  <I {...p}>
    <path d="m18 15-6-6-6 6" />
  </I>
);
const IconChevRight = (p: IconProps) => (
  <I {...p}>
    <path d="m9 6 6 6-6 6" />
  </I>
);
const IconChevLeft = (p: IconProps) => (
  <I {...p}>
    <path d="m15 18-6-6 6-6" />
  </I>
);
const IconArrowUp = (p: IconProps) => (
  <I {...p}>
    <path d="M12 19V5m0 0-7 7m7-7 7 7" />
  </I>
);
const IconArrowDown = (p: IconProps) => (
  <I {...p}>
    <path d="M12 5v14m0 0 7-7m-7 7-7-7" />
  </I>
);
const IconArrowRight = (p: IconProps) => (
  <I {...p}>
    <path d="M5 12h14m0 0-6-6m6 6-6 6" />
  </I>
);
const IconExternal = (p: IconProps) => (
  <I {...p}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <path d="M15 3h6v6" />
    <path d="m10 14 11-11" />
  </I>
);

// Status
const IconCheckCircle = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8 12 3 3 5-6" />
  </I>
);
const IconAlert = (p: IconProps) => (
  <I {...p}>
    <path d="m10.3 3.86-8.5 14.7A2 2 0 0 0 3.5 21.5h17a2 2 0 0 0 1.7-2.94L13.7 3.86a2 2 0 0 0-3.4 0" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </I>
);
const IconInfo = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </I>
);
const IconClock = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </I>
);
const IconCircle = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="9" />
  </I>
);
const IconCircleDot = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="2.5" fill="currentColor" />
  </I>
);
const IconBan = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m5.5 5.5 13 13" />
  </I>
);

// Domain
const IconGlobe = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18" />
  </I>
);
const IconLanguages = (p: IconProps) => (
  <I {...p}>
    <path d="m5 8 6 6" />
    <path d="m4 14 6-6 2-3" />
    <path d="M2 5h12" />
    <path d="M7 2h1" />
    <path d="m22 22-5-10-5 10" />
    <path d="M14 18h6" />
  </I>
);
const IconCoin = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M14.5 9.5a3 3 0 0 0-5 0V12a3 3 0 0 0 5 0v-2.5z" />
  </I>
);
const IconTag = (p: IconProps) => (
  <I {...p}>
    <path d="m20.59 13.41-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82" />
    <path d="M7 7h.01" />
  </I>
);
const IconStar = (p: IconProps) => (
  <I {...p}>
    <path d="m12 2 3 7 7 .5-5.5 5L18 22l-6-4-6 4 1.5-7.5L2 9.5 9 9z" />
  </I>
);
const IconHeart = (p: IconProps) => (
  <I {...p}>
    <path d="M19 14c1.5-1.5 3-3.5 3-5.5A5.5 5.5 0 0 0 16.5 3c-2 0-3 1-4.5 2.5C10.5 4 9.5 3 7.5 3A5.5 5.5 0 0 0 2 8.5c0 2 1.5 4 3 5.5l7 7z" />
  </I>
);
const IconLock = (p: IconProps) => (
  <I {...p}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </I>
);
const IconMail = (p: IconProps) => (
  <I {...p}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 6-10 7L2 6" />
  </I>
);
const IconImg = (p: IconProps) => (
  <I {...p}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-5-5L5 21" />
  </I>
);
const IconShield = (p: IconProps) => (
  <I {...p}>
    <path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5z" />
  </I>
);
const IconFlame = (p: IconProps) => (
  <I {...p}>
    <path d="M8 14c-2-3 0-7 4-9 0 3 1 5 3 6.5C17 13 18 15 18 17a6 6 0 1 1-12 0c0-1 1-2 2-3z" />
  </I>
);
const IconCalendar = (p: IconProps) => (
  <I {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M8 3v4M16 3v4" />
  </I>
);
const IconDot = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </I>
);
const IconGrip = (p: IconProps) => (
  <I {...p}>
    <circle cx="9" cy="6" r="1" fill="currentColor" />
    <circle cx="9" cy="12" r="1" fill="currentColor" />
    <circle cx="9" cy="18" r="1" fill="currentColor" />
    <circle cx="15" cy="6" r="1" fill="currentColor" />
    <circle cx="15" cy="12" r="1" fill="currentColor" />
    <circle cx="15" cy="18" r="1" fill="currentColor" />
  </I>
);
const IconLogout = (p: IconProps) => (
  <I {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </I>
);
const IconCmd = (p: IconProps) => (
  <I {...p}>
    <path d="M18 3h-1a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h1a3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3h1a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3" />
  </I>
);
const IconSun = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v2M12 19v2M5 5l1.5 1.5M17.5 17.5 19 19M3 12h2M19 12h2M5 19l1.5-1.5M17.5 6.5 19 5" />
  </I>
);
const IconMoon = (p: IconProps) => (
  <I {...p}>
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8" />
  </I>
);
const IconMonitor = (p: IconProps) => (
  <I {...p}>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
  </I>
);
const IconSparkles = (p: IconProps) => (
  <I {...p}>
    <path d="m12 3 1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" />
    <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8z" />
  </I>
);
const IconZap = (p: IconProps) => (
  <I {...p}>
    <path d="M13 2 3 14h7l-1 8 10-12h-7z" />
  </I>
);
const IconPause = (p: IconProps) => (
  <I {...p}>
    <rect x="6" y="5" width="3.5" height="14" rx="1" />
    <rect x="14.5" y="5" width="3.5" height="14" rx="1" />
  </I>
);
const IconPlay = (p: IconProps) => (
  <I {...p}>
    <path d="M7 4v16l13-8z" fill="currentColor" />
  </I>
);
const IconPaperPlane = (p: IconProps) => (
  <I {...p}>
    <path d="m3 11 18-8-8 18-2-8z" />
  </I>
);
const IconBuilding = (p: IconProps) => (
  <I {...p}>
    <rect x="4" y="3" width="16" height="18" rx="1" />
    <path d="M9 8h.01M9 12h.01M9 16h.01M15 8h.01M15 12h.01M15 16h.01" />
  </I>
);
const IconLayout = (p: IconProps) => (
  <I {...p}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18M9 3v18" />
  </I>
);
const IconList = (p: IconProps) => (
  <I {...p}>
    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
  </I>
);
const IconFolder = (p: IconProps) => (
  <I {...p}>
    <path d="M3 5a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </I>
);
const IconType = (p: IconProps) => (
  <I {...p}>
    <path d="M4 7V5h16v2" />
    <path d="M9 19h6M12 5v14" />
  </I>
);
const IconBranch = (p: IconProps) => (
  <I {...p}>
    <circle cx="6" cy="3" r="2" />
    <circle cx="6" cy="18" r="2" />
    <circle cx="18" cy="6" r="2" />
    <path d="M6 5v8a4 4 0 0 0 4 4h2a4 4 0 0 0 4-4V8" />
  </I>
);
const IconHistory = (p: IconProps) => (
  <I {...p}>
    <path d="M3 12a9 9 0 1 0 9-9 9 9 0 0 0-7 3.5" />
    <path d="M3 4v4h4" />
    <path d="M12 8v4l3 2" />
  </I>
);
// 支付通道商店图标 + 关联（支付产品重设计移植自 design/Novel-admin/src/icons.jsx）
const IconApple = (p: IconProps) => (
  <I {...p}>
    <path
      fill="currentColor"
      stroke="none"
      d="M16 13.1c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.7-1.7-3.3-1.8-1.4-.1-2.7.8-3.4.8s-1.8-.8-2.9-.8c-1.5 0-2.9.9-3.7 2.2-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.6 2.3 2.8 2.2 1.1 0 1.6-.7 2.9-.7s1.7.7 2.9.7c1.2 0 1.9-1.1 2.7-2.1.8-1.2 1.2-2.4 1.2-2.5-.1 0-2.3-.9-2.3-3.5z"
    />
    <path
      fill="currentColor"
      stroke="none"
      d="M13.8 6.1c.6-.8 1-1.8.9-2.9-.9.1-2 .6-2.7 1.4-.6.7-1.1 1.7-.9 2.7 1 .1 2-.5 2.7-1.2z"
    />
  </I>
);
const IconGooglePlay = (p: IconProps) => (
  <I {...p}>
    <path d="M4 3.2v17.6a.9.9 0 0 0 1.4.77l13.9-8.8a.9.9 0 0 0 0-1.54L5.4 2.43A.9.9 0 0 0 4 3.2z" />
    <path d="m4.6 3 9.6 9-9.6 9" />
  </I>
);
const IconLink = (p: IconProps) => (
  <I {...p}>
    <path d="M9 15l6-6" />
    <path d="M11.5 6.5 13 5a4 4 0 0 1 6 6l-1.5 1.5" />
    <path d="M12.5 17.5 11 19a4 4 0 0 1-6-6l1.5-1.5" />
  </I>
);

export const Icons = {
  IconHome,
  IconApple,
  IconGooglePlay,
  IconLink,
  IconApps,
  IconBook,
  IconWallet,
  IconUsers,
  IconMsg,
  IconBell,
  IconLayers,
  IconChart,
  IconCog,
  IconSearch,
  IconPlus,
  IconCheck,
  IconX,
  IconFilter,
  IconDownload,
  IconUpload,
  IconMore,
  IconMoreV,
  IconEdit,
  IconTrash,
  IconEye,
  IconEyeOff,
  IconCopy,
  IconRefresh,
  IconChevDown,
  IconChevUp,
  IconChevRight,
  IconChevLeft,
  IconArrowUp,
  IconArrowDown,
  IconArrowRight,
  IconExternal,
  IconCheckCircle,
  IconAlert,
  IconInfo,
  IconClock,
  IconCircle,
  IconCircleDot,
  IconBan,
  IconGlobe,
  IconLanguages,
  IconCoin,
  IconTag,
  IconStar,
  IconHeart,
  IconLock,
  IconMail,
  IconImg,
  IconShield,
  IconFlame,
  IconCalendar,
  IconDot,
  IconGrip,
  IconLogout,
  IconCmd,
  IconSun,
  IconMoon,
  IconMonitor,
  IconSparkles,
  IconZap,
  IconPause,
  IconPlay,
  IconPaperPlane,
  IconBuilding,
  IconLayout,
  IconList,
  IconFolder,
  IconType,
  IconBranch,
  IconHistory,
} as const;

export type IconName = keyof typeof Icons;
